/**
 * The upload-first photo slots (phase 5 doc, "How attachment_ids maps to
 * stored files"; phase 6 doc, "Photo capture and the upload-first flow").
 *
 * Each of the three slots uploads the moment a file is chosen, not when the
 * form is submitted; a form then sends only the ids those uploads returned.
 * A failed slot (413 too large, 415 wrong type) shows the server's message
 * and offers a retry input in place, without touching the other slots; the
 * owning form disables Submit while any slot is uploading.
 *
 * Shared by every form's submit screen (Cash Drop and Safe Count as of the
 * 2026-09-07 consolidation, docs/phases/petty-cash-consolidation.md Part 1;
 * Petty Cash next). A form supplies its own upload call and legend, and may
 * pass `maxSlots` when it wants fewer than the default three; nothing else
 * about the slots is per-form.
 */
import { useState, type ChangeEvent } from "react";

import { ApiError, type AttachmentOut } from "../api/public";
import type { TokenGetter } from "../auth/session";

/** Default slot count; the phase 5 cap for Cash Drop and Safe Count. */
export const MAX_SLOTS = 3;

export type Slot =
  | { status: "empty" }
  | { status: "uploading"; fileName: string }
  | { status: "done"; attachmentId: string; fileName: string }
  | { status: "error"; fileName: string; message: string };

function isDone(slot: Slot): slot is Extract<Slot, { status: "done" }> {
  return slot.status === "done";
}

function emptySlots(count: number): Slot[] {
  return Array.from({ length: count }, (): Slot => ({ status: "empty" }));
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return String(error);
}

/** One of the per-form upload calls in `api/public.ts` (`uploadAttachment`,
 * `uploadSafeCountAttachment`) - same request, different prefix. */
export type Uploader = (file: File, token?: string) => Promise<AttachmentOut>;

export function useAttachmentSlots(
  upload: Uploader,
  getToken: TokenGetter,
  { maxSlots = MAX_SLOTS }: { maxSlots?: number } = {},
) {
  const [slots, setSlots] = useState<Slot[]>(() => emptySlots(maxSlots));

  const attachmentIds = slots.filter(isDone).map((slot) => slot.attachmentId);
  const anyUploading = slots.some((slot) => slot.status === "uploading");

  async function handleFileChosen(index: number, file: File) {
    setSlots((prev) =>
      prev.map((slot, i) => (i === index ? { status: "uploading", fileName: file.name } : slot)),
    );
    try {
      const token = await getToken();
      const result = await upload(file, token);
      setSlots((prev) =>
        prev.map((slot, i) =>
          i === index
            ? { status: "done", attachmentId: result.attachment_id, fileName: result.original_filename }
            : slot,
        ),
      );
    } catch (error) {
      setSlots((prev) =>
        prev.map((slot, i) =>
          i === index ? { status: "error", fileName: file.name, message: errorMessage(error) } : slot,
        ),
      );
    }
  }

  function handleSlotInputChange(index: number, event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Reset the input so choosing the SAME file again (a retry after a 413/415
    // with no other file handy) still fires a change event.
    event.target.value = "";
    if (file) void handleFileChosen(index, file);
  }

  function clearSlot(index: number) {
    setSlots((prev) => prev.map((slot, i) => (i === index ? { status: "empty" } : slot)));
  }

  function reset() {
    setSlots(emptySlots(maxSlots));
  }

  return { slots, attachmentIds, anyUploading, handleSlotInputChange, clearSlot, reset };
}

export function AttachmentSlots({
  legend,
  slots,
  onChange,
  onClear,
}: {
  legend: string;
  slots: Slot[];
  onChange: (index: number, event: ChangeEvent<HTMLInputElement>) => void;
  onClear: (index: number) => void;
}) {
  return (
    <fieldset className="attachment-slots">
      <legend>{legend}</legend>
      {slots.map((slot, index) => (
        <AttachmentSlot
          key={index}
          slot={slot}
          onChange={(e) => onChange(index, e)}
          onClear={() => onClear(index)}
        />
      ))}
    </fieldset>
  );
}

function AttachmentSlot({
  slot,
  onChange,
  onClear,
}: {
  slot: Slot;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
}) {
  if (slot.status === "uploading") {
    return (
      <div className="attachment-slot" data-testid="attachment-slot" data-slot-status="uploading">
        <span className="muted">Uploading {slot.fileName}…</span>
      </div>
    );
  }

  if (slot.status === "done") {
    return (
      <div className="attachment-slot attachment-slot--done" data-testid="attachment-slot" data-slot-status="done">
        <span>{slot.fileName}</span>
        <button type="button" onClick={onClear}>
          Remove
        </button>
      </div>
    );
  }

  if (slot.status === "error") {
    return (
      <div
        className="attachment-slot attachment-slot--error"
        data-testid="attachment-slot"
        data-slot-status="error"
      >
        <span className="field-error">
          {slot.fileName}: {slot.message}
        </span>
        <label className="file-retry">
          Retry
          <input type="file" accept="image/*" capture="environment" onChange={onChange} />
        </label>
      </div>
    );
  }

  return (
    <div className="attachment-slot" data-testid="attachment-slot" data-slot-status="empty">
      <input type="file" accept="image/*" capture="environment" onChange={onChange} />
    </div>
  );
}
